import { useEffect, useRef, useState } from "react";
import { useCommunicator } from "@/contexts/CommunicatorContext";
import { useSubscription } from "@/hooks/useSubscription";
import { debugLog, debugWarn } from "@/utils";

export interface FullscreenWidgetProps {
	backgroundColor?: string;
	children?: React.ReactNode;
	autoRequest?: boolean;
	exitOnUnmount?: boolean;
	openIntentId?: number;

	// Layering invariants: popup surfaces must be above widget surfaces.
	zIndex?: number;
	className?: string;
}

export function FullscreenWidget({
	backgroundColor = "rgba(0, 0, 0, 0.85)",
	children,
	autoRequest = true,
	exitOnUnmount = true,
	openIntentId,
	zIndex,
	className,
}: FullscreenWidgetProps) {
	const {
		demandFullscreen,
		onFullscreenGranted,
		exitFullscreen,
		onExitFullscreenAck,
	} = useCommunicator();

	const [granted, setGranted] = useState(false);

	const requestedRef = useRef(false);
	const exitingRef = useRef(false);
	const mountedRef = useRef(false);
	const lastDemandedOpenIntentIdRef = useRef<number | null>(null);
	const instanceIdRef = useRef(
		`FullscreenWidget-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
	);

	useEffect(() => {
		mountedRef.current = true;

		debugLog("[FullscreenWidget] mounted", {
			instanceId: instanceIdRef.current,
			autoRequest,
			exitOnUnmount,
			openIntentId: openIntentId ?? null,
			backgroundColor,
			zIndex: zIndex ?? null,
			className: className ?? null,
		});

		return () => {
			mountedRef.current = false;

			debugLog("[FullscreenWidget] unmounting", {
				instanceId: instanceIdRef.current,
				granted,
				requested: requestedRef.current,
				openIntentId: openIntentId ?? null,
				lastDemandedOpenIntentId: lastDemandedOpenIntentIdRef.current,
				exiting: exitingRef.current,
				exitOnUnmount,
			});
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		debugLog("[FullscreenWidget] props/state snapshot", {
			instanceId: instanceIdRef.current,
			autoRequest,
			exitOnUnmount,
			openIntentId: openIntentId ?? null,
			lastDemandedOpenIntentId: lastDemandedOpenIntentIdRef.current,
			granted,
			requested: requestedRef.current,
			exiting: exitingRef.current,
			hasChildren: !!children,
		});
	}, [autoRequest, children, exitOnUnmount, granted, openIntentId]);

	useSubscription(
		onFullscreenGranted,
		() => {
			debugLog("[FullscreenWidget] host -> FullscreenGranted", {
				instanceId: instanceIdRef.current,
				beforeGranted: granted,
				requested: requestedRef.current,
				openIntentId: openIntentId ?? null,
				lastDemandedOpenIntentId: lastDemandedOpenIntentIdRef.current,
				exiting: exitingRef.current,
				mounted: mountedRef.current,
			});

			setGranted(true);
			exitingRef.current = false;
		},
		[granted, openIntentId]
	);

	useSubscription(
		onExitFullscreenAck,
		() => {
			debugLog("[FullscreenWidget] host -> ExitFullscreenAck", {
				instanceId: instanceIdRef.current,
				beforeGranted: granted,
				requested: requestedRef.current,
				openIntentId: openIntentId ?? null,
				lastDemandedOpenIntentId: lastDemandedOpenIntentIdRef.current,
				exiting: exitingRef.current,
				mounted: mountedRef.current,
			});

			setGranted(false);
			exitingRef.current = false;
			requestedRef.current = false;
		},
		[granted, openIntentId]
	);

	useEffect(() => {
		return () => {
			if (!exitOnUnmount) {
				debugLog("[FullscreenWidget] cleanup skipped exitFullscreen because exitOnUnmount=false", {
					instanceId: instanceIdRef.current,
					granted,
					requested: requestedRef.current,
					openIntentId: openIntentId ?? null,
					lastDemandedOpenIntentId: lastDemandedOpenIntentIdRef.current,
					exiting: exitingRef.current,
				});
				return;
			}

			if (!exitingRef.current && granted) {
				exitingRef.current = true;

				debugLog("[FullscreenWidget] cleanup -> exitFullscreen()", {
					instanceId: instanceIdRef.current,
					granted,
					requested: requestedRef.current,
					openIntentId: openIntentId ?? null,
					lastDemandedOpenIntentId: lastDemandedOpenIntentIdRef.current,
				});

				exitFullscreen();
				return;
			}

			debugLog("[FullscreenWidget] cleanup did not call exitFullscreen()", {
				instanceId: instanceIdRef.current,
				granted,
				requested: requestedRef.current,
				openIntentId: openIntentId ?? null,
				lastDemandedOpenIntentId: lastDemandedOpenIntentIdRef.current,
				exiting: exitingRef.current,
				reason: !granted ? "not-granted" : "already-exiting",
			});
		};
	}, [exitFullscreen, exitOnUnmount, granted, openIntentId]);

	useEffect(() => {
		const hasOpenIntent = typeof openIntentId === "number";
		const openIntentAlreadyDemanded =
			hasOpenIntent && lastDemandedOpenIntentIdRef.current === openIntentId;

		if (!autoRequest) {
			debugLog("[FullscreenWidget] autoRequest=false -> demandFullscreen() not sent", {
				instanceId: instanceIdRef.current,
				requested: requestedRef.current,
				openIntentId: openIntentId ?? null,
				lastDemandedOpenIntentId: lastDemandedOpenIntentIdRef.current,
				granted,
			});
			return;
		}

		if (granted) {
			debugLog("[FullscreenWidget] demandFullscreen() skipped because fullscreen is already granted", {
				instanceId: instanceIdRef.current,
				requested: requestedRef.current,
				openIntentId: openIntentId ?? null,
				lastDemandedOpenIntentId: lastDemandedOpenIntentIdRef.current,
			});
			return;
		}

		if (!hasOpenIntent && requestedRef.current) {
			debugLog("[FullscreenWidget] demandFullscreen() skipped because already requested", {
				instanceId: instanceIdRef.current,
				granted,
				exiting: exitingRef.current,
			});
			return;
		}

		if (openIntentAlreadyDemanded) {
			debugLog("[FullscreenWidget] demandFullscreen() skipped because open intent was already handled", {
				instanceId: instanceIdRef.current,
				granted,
				exiting: exitingRef.current,
				openIntentId,
				lastDemandedOpenIntentId: lastDemandedOpenIntentIdRef.current,
			});
			return;
		}

		requestedRef.current = true;

		if (hasOpenIntent) {
			lastDemandedOpenIntentIdRef.current = openIntentId;
		}

		debugLog("[FullscreenWidget] -> demandFullscreen()", {
			instanceId: instanceIdRef.current,
			granted,
			exiting: exitingRef.current,
			mounted: mountedRef.current,
			openIntentId: openIntentId ?? null,
			lastDemandedOpenIntentId: lastDemandedOpenIntentIdRef.current,
			hasOpenIntent,
		});

		try {
			demandFullscreen();
		} catch (error) {
			if (hasOpenIntent) {
				lastDemandedOpenIntentIdRef.current = null;
			}

			requestedRef.current = false;

			debugWarn("[FullscreenWidget] demandFullscreen() threw", {
				instanceId: instanceIdRef.current,
				openIntentId: openIntentId ?? null,
				error: error instanceof Error ? error.message : String(error),
			});
			throw error;
		}
	}, [autoRequest, demandFullscreen, granted, openIntentId]);

	if (!granted) {
		debugLog("[FullscreenWidget] render -> null because granted=false", {
			instanceId: instanceIdRef.current,
			requested: requestedRef.current,
			openIntentId: openIntentId ?? null,
			lastDemandedOpenIntentId: lastDemandedOpenIntentIdRef.current,
			exiting: exitingRef.current,
		});

		return null;
	}

	debugLog("[FullscreenWidget] render -> fullscreen container", {
		instanceId: instanceIdRef.current,
		requested: requestedRef.current,
		openIntentId: openIntentId ?? null,
		lastDemandedOpenIntentId: lastDemandedOpenIntentIdRef.current,
		exiting: exitingRef.current,
	});

	return (
		<div
			className={`fixed inset-0 flex h-screen w-screen items-center justify-center pointer-events-auto ${className ?? ""}`}
			style={{ backgroundColor, zIndex }}
			aria-label="FullscreenWidget"
		>
			{children}
		</div>
	);
}